import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import PropertyMap from '../components/PropertyMap';
import alertsService from '../services/alertsService';
import propertiesService from '../services/propertiesService';
import { useToast } from '../contexts/ToastContext';
import './IncidentMap.css';

function IncidentMap() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const messagesContainerRef = useRef(null);

  const [alert, setAlert] = useState(null);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationStatus, setLocationStatus] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [mapZoom, setMapZoom] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [stagingAreas, setStagingAreas] = useState([]);
  const [stagingLoading, setStagingLoading] = useState(false);

  const handleMapChange = useCallback(({ zoom, center }) => {
    setMapZoom(zoom);
    setMapCenter(center);
  }, []);

  const incidentPoint = (() => {
    if (property?.boundary?.length) {
      const pts = property.boundary;
      return {
        lat: pts.reduce((s, p) => s + p.lat, 0) / pts.length,
        lng: pts.reduce((s, p) => s + p.lng, 0) / pts.length,
      };
    }
    if (alert?.lat != null && alert?.lng != null) {
      return { lat: alert.lat, lng: alert.lng };
    }
    return null;
  })();

  useEffect(() => {
    async function load() {
      try {
        const a = await alertsService.getAlert(id);
        setAlert(a);
        if (a.property_id) {
          const p = await propertiesService.getProperty(a.property_id);
          setProperty(p);
        }
      } catch (e) {
        addToast('Could not load incident map.', 'error');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, addToast]);

  useEffect(() => {
    async function loadStaging() {
      if (!incidentPoint) return;
      setStagingLoading(true);
      try {
        const areas = await propertiesService.getStagingProperties(incidentPoint, 50, alert?.property_id);
        setStagingAreas(areas);
      } catch (e) {
        console.warn('Failed to load staging areas:', e);
      } finally {
        setStagingLoading(false);
      }
    }
    loadStaging();
  }, [incidentPoint, alert?.property_id]);

  useEffect(() => {
    async function loadMessages() {
      try {
        const m = await alertsService.getMessages(id);
        setMessages(m);
      } catch (e) {
        console.warn('Failed to load messages:', e);
      }
    }
    loadMessages();
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    async function loadLocations() {
      try {
        const l = await alertsService.getLocations(id);
        setLocations(l);
      } catch (e) {
        console.warn('Failed to load locations:', e);
      }
    }
    loadLocations();
    const interval = setInterval(loadLocations, 5000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationStatus('Geolocation not supported');
      return;
    }
    let watchId;
    setLocationStatus('Requesting location…');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        alertsService.postLocation(id, latitude, longitude).catch(() => {});
        setLocationStatus('Sharing location');
      },
      (error) => {
        setLocationStatus(error.code === 1 ? 'Location permission denied' : 'Location unavailable');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        alertsService.postLocation(id, latitude, longitude).catch(() => {});
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 10000 }
    );
    return () => {
      if (watchId !== undefined) navigator.geolocation.clearWatch(watchId);
    };
  }, [id]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await alertsService.postMessage(id, newMessage.trim());
      setNewMessage('');
      const m = await alertsService.getMessages(id);
      setMessages(m);
    } catch (e) {
      addToast('Failed to send message.', 'error');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="incident-map-page"><div className="container"><p>Loading…</p></div></div>;
  if (!alert) return <div className="incident-map-page"><div className="container"><p>Alert not found.</p></div></div>;

  return (
    <div className="incident-map-page">
      <div className="container">
        <div className="incident-header">
          <h1>Incident Map: {alert.type}</h1>
          <button className="back-button" onClick={() => navigate(`/alerts/${id}`)}>
            Back to Alert
          </button>
        </div>
        <div className="incident-map">
          <PropertyMap
            boundary={property?.boundary}
            features={property?.features}
            locations={locations}
            zoom={mapZoom}
            center={mapCenter}
            onMapChange={handleMapChange}
          />
        </div>

        <div className="chat-section">
          <div className="chat-header">
            <h2>Incident Chat</h2>
            {locationStatus && <span className="location-status">{locationStatus}</span>}
          </div>
          <div ref={messagesContainerRef} className="messages">
            {messages.length === 0 && <p className="no-messages">No messages yet.</p>}
            {messages.map(m => (
              <div key={m.id} className="message">
                <div className="message-meta">
                  <span className="message-author">{m.name}</span>
                  <span className="message-time">{new Date(m.created_at).toLocaleTimeString()}</span>
                </div>
                <p className="message-text">{m.message}</p>
              </div>
            ))}
          </div>
          <form className="chat-form" onSubmit={handleSend}>
            <input
              type="text"
              className="chat-input"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              disabled={sending}
            />
            <button type="submit" className="chat-send" disabled={sending || !newMessage.trim()}>
              {sending ? 'Sending…' : 'Send'}
            </button>
          </form>
        </div>

        <div className="staging-section">
          <h2>Nearby Livestock Staging Areas</h2>
          {stagingLoading ? (
            <p className="staging-status">Loading…</p>
          ) : stagingAreas.length === 0 ? (
            <p className="staging-status">No livestock staging areas found within 50 miles.</p>
          ) : (
            <ul className="staging-list">
              {stagingAreas.map(area => (
                <li key={area.id} className="staging-card">
                  <button
                    type="button"
                    className="staging-name"
                    onClick={() => navigate(`/properties/${area.id}`)}
                  >
                    {area.name}
                  </button>
                  <span className="staging-distance">{area.distance_miles} mi</span>
                  {area.livestock_staging_notes && (
                    <p className="staging-notes-preview">{area.livestock_staging_notes}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default IncidentMap;
